import React from 'react';
import { styled } from '@compiled/react';
import { isMacOs } from '@jira/platform__os-detection/src/isMacOs.tsx';
import { token } from '@atlaskit/tokens';
import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { components } from '@atlassian/jira-command-palette/src/common/ui/components/index.tsx';
import { OutlinedLozenge } from '@atlassian/jira-command-palette/src/common/ui/custom-lozenge/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { messages } from './messages.tsx';
import type { NewTabHintMessageProps } from './types.tsx';

const styles = cssMap({
	footerContentContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
	},
});

export const NewTabHintMessage = ({
	defaultFooterContent,
}: NewTabHintMessageProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const footerContent = (
		<>
			<div>{defaultFooterContent}</div>
			<HintMessageWrapper>
				<MessageWrapper>{formatMessage(messages.openInANewTab)}</MessageWrapper>
				{isMacOs() ? (
					<MacLozenge>
						<MacTextOffset>⌘</MacTextOffset>
					</MacLozenge>
				) : (
					<WindowsLozenge>{formatMessage(messages.ctrlLozenge)}</WindowsLozenge>
				)}
				<EnterLozenge>
					<EnterIconSVG />
				</EnterLozenge>
			</HintMessageWrapper>
		</>
	);

	return (
		<components.Footer>
			{fg('compal_jifjam_navigation_new_filters') ? (
				<Box
					xcss={styles.footerContentContainer}
					testId="command-palette-common.common.ui.new-tab-hint-message"
				>
					{footerContent}
				</Box>
			) : (
				<FooterContentContainerOld data-testid="command-palette-common.common.ui.new-tab-hint-message">
					{footerContent}
				</FooterContentContainerOld>
			)}
		</components.Footer>
	);
};

const EnterIconSVG = () => (
	<svg width="13" height="8" viewBox="0 0 13 8" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M11.055 1.055V3.055H2.275L4.195 1.135C4.455 0.875 4.455 0.455 4.195 0.195C3.935 -0.065 3.515 -0.065 3.255 0.195L0.195 3.255C-0.065 3.515 -0.065 3.935 0.195 4.195L3.255 7.255C3.515 7.515 3.935 7.515 4.195 7.255C4.455 6.995 4.455 6.575 4.195 6.315L2.275 4.38833H11.7217C12.0883 4.38833 12.3883 4.08833 12.3883 3.72167V1.055C12.3883 0.688333 12.0883 0.388333 11.7217 0.388333C11.355 0.388333 11.055 0.688333 11.055 1.055Z"
			fill={token('color.text.subtle')}
		/>
	</svg>
);

// Delete when cleaning up compal_jifjam_navigation_new_filters
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterContentContainerOld = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HintMessageWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-start',

	color: token('color.text.subtle'),
	paddingRight: token('space.075'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MacLozenge = styled(OutlinedLozenge)({
	marginLeft: token('space.025'),
	font: token('font.body.large'),
	lineHeight: '16px',

	color: token('color.text.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MacTextOffset = styled.p({
	transform: 'translateY(0.5px)',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WindowsLozenge = styled(OutlinedLozenge)({
	width: '37px',
	fontWeight: token('font.weight.regular'),
	textTransform: 'capitalize',

	color: token('color.text.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EnterLozenge = styled(OutlinedLozenge)({
	marginLeft: token('space.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageWrapper = styled.div({
	font: token('font.body.UNSAFE_small'),
	marginRight: token('space.025'),
});
